import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import collectionsService from '../services/collectionsService';
import {Collection} from '../types/Collection';
import {handleError} from '../../../common/utils/errorHandling';
import AdminFormHeader from '../../../layout/admin/AdminFormHeader';
import {AdminButton, AdminForm, AdminFormSection, AdminInputText, AdminRichTextArea} from '../../../layout/admin';
import {Checkbox} from 'primereact/checkbox';
import CollectionItemsTable from './CollectionItemsTable';
import './CollectionForm.scss';
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import FieldLanguageSwitcher from '../../../layout/admin/FieldLanguageSwitcher';
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

interface CollectionFormData {
  personal: boolean;
  published: boolean;
}

const CollectionForm: React.FC = () => {
  const {t} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(false);
  const isEditMode = !!id;

  const titleField = useTranslatableField();
  const subtitleField = useTranslatableField();
  const descriptionField = useTranslatableField();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [formData, setFormData] = useState<CollectionFormData>({
    personal: true,
    published: false
  });

  useEffect(() => {
    if (isEditMode && id) {
      fetchCollection(id);
    }
  }, [id, isEditMode]);

  const fetchCollection = async (collectionId: string): Promise<void> => {
    await handleError(
      async () => {
        const collectionData = await collectionsService.getCollection(collectionId);
        setCollection(collectionData);
        titleField.setTranslations(collectionData.title || {});
        subtitleField.setTranslations(collectionData.subtitle || {});
        descriptionField.setTranslations(collectionData.description || {});
        setFormData({
          personal: collectionData.personal,
          published: collectionData.published
        });
      },
      t('admin.collections.errors.fetching')
    );
  };

  const handleInputChange = (field: keyof CollectionFormData, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      if (isEditMode && collection) {
        const updatedCollection: Collection = {
          id: collection.id,
          title: titleField.translations,
          subtitle: subtitleField.translations,
          description: descriptionField.translations,
          personal: formData.personal,
          published: formData.published,
          favourite: collection.favourite
        };
        await collectionsService.updateCollection(updatedCollection);
      } else {
        await collectionsService.createCollection({
          title: titleField.translations,
          subtitle: subtitleField.translations,
          description: descriptionField.translations,
          personal: formData.personal,
          published: formData.published,
          favourite: false
        });
      }
      navigate('/collections');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="collection-form">
      <AdminFormHeader
        title={collection ? t('admin.collections.title.edit') : t('admin.collections.title.create')}
        subtitle={t('admin.collections.subtitle.form')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={titleField.currentValue}
                onChange={(e) => titleField.setCurrentValue(e.target.value)}
                name="title"
                id="title"
                label={t('admin.collections.fields.title')}
                required={true}
                error={errors.title}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={titleField.currentLanguage}
                    onLanguageChange={titleField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={titleField.translatedLanguages}
                  />
                }
              />

              <AdminInputText
                value={subtitleField.currentValue}
                onChange={(e) => subtitleField.setCurrentValue(e.target.value)}
                name="subtitle"
                id="subtitle"
                label={t('admin.collections.fields.subtitle')}
                error={errors.subtitle}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={subtitleField.currentLanguage}
                    onLanguageChange={subtitleField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={subtitleField.translatedLanguages}
                  />
                }
              />
            </AdminFormSection>

            <AdminFormSection title={t('admin.collections.sections.settings')}>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    inputId="personal"
                    checked={formData.personal}
                    onChange={(e) => handleInputChange('personal', e.checked || false)}
                  />
                  <label htmlFor="personal" className="cursor-pointer">{t('admin.collections.fields.personal')}</label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    inputId="published"
                    checked={formData.published}
                    onChange={(e) => handleInputChange('published', e.checked || false)}
                  />
                  <label htmlFor="published"
                         className="cursor-pointer">{t('admin.collections.fields.published')}</label>
                </div>
              </div>
            </AdminFormSection>
          </>
        }
        rightColumn={
          <AdminFormSection title={t('admin.collections.sections.details')}>
            <AdminRichTextArea
              value={descriptionField.currentValue}
              onChange={descriptionField.setCurrentValue}
              name="description"
              id="description"
              label={t('admin.common.fields.description')}
              error={errors.description}
              languageSwitcher={
                <FieldLanguageSwitcher
                  currentLanguage={descriptionField.currentLanguage}
                  onLanguageChange={descriptionField.setCurrentLanguage}
                  collapsed={true}
                  translatedLanguages={descriptionField.translatedLanguages}
                />
              }
            />
          </AdminFormSection>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate('/collections')}
            />
            <AdminButton
              type="submit"
              label={collection ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      >
        {isEditMode && collection && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold mb-4">{t('admin.collections.sections.collectionItems')}</h3>
            <CollectionItemsTable collectionId={collection.id} />
          </div>
        )}
      </AdminForm>
    </div>
  );
};

export default CollectionForm;


